// Tabella classifica tecnici — componente riutilizzabile per la leaderboard gamification
import { Star, Target } from 'lucide-react'
import { Avatar } from '../../../components/ui/Avatar'
import { Skeleton } from '../../../components/ui/Skeleton'
import { Card, CardHeader, CardTitle } from '../../../components/ui/Card'

export interface LeaderboardEntry {
  rank: number
  userId: string
  name: string
  levelName: string
  points: number
  completedTickets: number
  monthlyReports: number
  monthlySlaRespect: number
  badges: Array<{ id: string; name: string; icon: string }>
  monthlyGoalProgress: number
  avgRating: number
}

interface TechnicianRankingProps {
  entries: LeaderboardEntry[]
  isLoading?: boolean
}

function rankMedal(rank: number): string {
  if (rank === 1) return '🥇'
  if (rank === 2) return '🥈'
  if (rank === 3) return '🥉'
  return `#${rank}`
}

export function TechnicianRanking({ entries, isLoading = false }: TechnicianRankingProps) {
  return (
    <>
      {/* Podio top 3 */}
      {!isLoading && entries.length >= 3 && (
        <div className="flex items-end justify-center gap-4 py-6" aria-label="Podio top 3">
          {/* 2° posto */}
          <div className="flex flex-col items-center gap-2">
            <Avatar name={entries[1]?.name ?? ''} size="lg" />
            <div className="flex h-20 w-24 flex-col items-center justify-center rounded-t-lg bg-[var(--fc-gray-200)] text-center">
              <span className="text-2xl" aria-hidden="true">🥈</span>
              <p className="text-xs font-semibold text-[var(--fc-foreground)]">{entries[1]?.points} pt</p>
            </div>
            <p className="text-xs font-medium text-[var(--fc-foreground-muted)] text-center max-w-[80px] truncate">
              {entries[1]?.name}
            </p>
          </div>

          {/* 1° posto */}
          <div className="flex flex-col items-center gap-2">
            <Avatar name={entries[0]?.name ?? ''} size="xl" />
            <div className="flex h-28 w-28 flex-col items-center justify-center rounded-t-lg bg-yellow-100 text-center">
              <span className="text-3xl" aria-hidden="true">🥇</span>
              <p className="text-sm font-bold text-[var(--fc-foreground)]">{entries[0]?.points} pt</p>
            </div>
            <p className="text-sm font-semibold text-[var(--fc-foreground)] text-center max-w-[96px] truncate">
              {entries[0]?.name}
            </p>
          </div>

          {/* 3° posto */}
          <div className="flex flex-col items-center gap-2">
            <Avatar name={entries[2]?.name ?? ''} size="lg" />
            <div className="flex h-16 w-24 flex-col items-center justify-center rounded-t-lg bg-orange-100 text-center">
              <span className="text-2xl" aria-hidden="true">🥉</span>
              <p className="text-xs font-semibold text-[var(--fc-foreground)]">{entries[2]?.points} pt</p>
            </div>
            <p className="text-xs font-medium text-[var(--fc-foreground-muted)] text-center max-w-[80px] truncate">
              {entries[2]?.name}
            </p>
          </div>
        </div>
      )}

      {/* Classifica completa */}
      <Card noPadding>
        <CardHeader className="px-4 pt-4">
          <CardTitle>Classifica completa</CardTitle>
        </CardHeader>

        {isLoading ? (
          <div className="space-y-3 px-4 pb-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton width={32} height={16} />
                <Skeleton width={40} height={40} rounded />
                <Skeleton height={14} className="flex-1" />
                <Skeleton width={60} height={14} />
              </div>
            ))}
          </div>
        ) : entries.length === 0 ? (
          <p className="px-4 pb-4 text-sm text-[var(--fc-foreground-muted)]">
            Nessun dato disponibile per il periodo selezionato.
          </p>
        ) : (
          <ol aria-label="Classifica tecnici">
            {entries.map((entry) => (
              <li
                key={entry.userId}
                className="flex items-center gap-4 border-b border-[var(--fc-border)] px-4 py-3 last:border-0"
              >
                {/* Posizione */}
                <span
                  className="w-8 shrink-0 text-center text-sm font-bold text-[var(--fc-foreground-muted)]"
                  aria-label={`Posizione ${entry.rank}`}
                >
                  {rankMedal(entry.rank)}
                </span>

                {/* Avatar */}
                <Avatar name={entry.name} size="md" />

                {/* Dati tecnico */}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-semibold text-[var(--fc-foreground)]">{entry.name}</p>
                  <div className="flex items-center gap-3 text-xs text-[var(--fc-foreground-muted)]">
                    <span>{entry.levelName}</span>
                    <span>{entry.completedTickets} ticket</span>
                    {entry.avgRating > 0 && (
                      <span className="flex items-center gap-0.5">
                        <Star className="h-3 w-3" aria-hidden="true" />
                        {entry.avgRating.toFixed(1)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Badge (max 3) */}
                {entry.badges.length > 0 && (
                  <div className="hidden sm:flex gap-1" aria-label="Badge guadagnati">
                    {entry.badges.slice(0, 3).map((badge) => (
                      <span
                        key={badge.id}
                        title={badge.name}
                        className="text-lg leading-none"
                        aria-label={badge.name}
                      >
                        {badge.icon}
                      </span>
                    ))}
                  </div>
                )}

                {/* Punti */}
                <div className="shrink-0 text-right">
                  <p className="text-lg font-bold text-[var(--fc-primary)]">{entry.points}</p>
                  <p className="text-xs text-[var(--fc-foreground-muted)]">punti</p>
                </div>

                {/* Progress obiettivo mensile */}
                <div className="hidden md:block w-24 shrink-0">
                  <div className="mb-1 flex items-center justify-between text-xs text-[var(--fc-foreground-muted)]">
                    <Target className="h-3 w-3" aria-hidden="true" />
                    <span>{entry.monthlyGoalProgress}%</span>
                  </div>
                  <div
                    className="h-1.5 rounded-full bg-[var(--fc-gray-200)]"
                    role="progressbar"
                    aria-valuenow={entry.monthlyGoalProgress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Progresso obiettivo mensile: ${entry.monthlyGoalProgress}%`}
                  >
                    <div
                      className="h-1.5 rounded-full bg-[var(--fc-primary)] transition-all"
                      style={{ width: `${Math.min(100, entry.monthlyGoalProgress)}%` }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </Card>
    </>
  )
}
